import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Trigger, TriggerType } from 'dashboard/models/trigger';
import { Workflow, WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { EventName } from 'lib/constants/events';
import Action from 'lib/interfaces/Action';
import { PaginationParams } from 'lib/interfaces/api';

export interface WorkflowsPayload {
  workflows: Workflow[];
}

export interface WorkflowPayload {
  workflow: Workflow;
}

export interface WorkflowVersionsPayload {
  workflowVersions: WorkflowVersion[];
}

export interface WorkflowDeploymentsPayload {
  workflowDeployments: WorkflowDeployment[];
}

export interface WorkflowTriggersPayload {
  triggers: Trigger[];
}

export interface WorkflowsFilterParams {
  triggerTypes?: TriggerType[];
  omni?: string;
  status?: WorkflowStatus[];
  workflowIds?: string[];
  eventNames?: EventName[];
}

interface SetFiltersPayload {
  filters: WorkflowsFilterParams;
}

interface SetSortPayload {
  sortParams: SortParams;
}

interface CurrentPagePayload {
  currentPage: number;
}

interface PageSizePayload {
  pageSize: number;
}

interface PaginationParamsPayload {
  paginationParams: PaginationParams;
}

export enum WorkflowsActionType {
  AddWorkflow = 'workflows/ADD_WORKFLOW',
  SetCurrentPage = 'workflows/SET_CURRENT_PAGE',
  SetPageSize = 'workflows/SET_PAGE_SIZE',
  SetPaginationParams = 'workflows/SET_PAGINATION_PARAMS',
  SetWorkflows = 'workflows/SET_WORKFLOWS',
  SetWorkflowVersions = 'workflows/SET_WORKFLOW_VERSIONS',
  SetWorkflowDeployments = 'workflows/SET_WORKFLOW_DEPLOYMENTS',
  SetTriggers = 'workflows/SET_TRIGGERS',
  SetFilters = 'workflows/SET_FILTERS',
  SetSort = 'workflows/SET_SORT',
}

export function addWorkflow(
  workflow: WorkflowPayload['workflow'],
): Action<WorkflowsActionType.AddWorkflow, WorkflowPayload> {
  return {
    payload: {
      workflow,
    },
    type: WorkflowsActionType.AddWorkflow,
  };
}

export function setWorkflows(
  workflows: WorkflowsPayload['workflows'],
): Action<WorkflowsActionType.SetWorkflows, WorkflowsPayload> {
  return {
    payload: {
      workflows,
    },
    type: WorkflowsActionType.SetWorkflows,
  };
}

export function setCurrentPage(
  currentPage: number,
): Action<WorkflowsActionType.SetCurrentPage, CurrentPagePayload> {
  return {
    payload: {
      currentPage,
    },
    type: WorkflowsActionType.SetCurrentPage,
  };
}

export function setPageSize(
  pageSize: number,
): Action<WorkflowsActionType.SetPageSize, PageSizePayload> {
  return {
    payload: {
      pageSize,
    },
    type: WorkflowsActionType.SetPageSize,
  };
}

export function setPaginationParams(
  paginationParams: PaginationParams,
): Action<WorkflowsActionType.SetPaginationParams, PaginationParamsPayload> {
  return {
    payload: {
      paginationParams,
    },
    type: WorkflowsActionType.SetPaginationParams,
  };
}

export function setWorkflowVersions(
  workflowVersions: WorkflowVersionsPayload['workflowVersions'],
): Action<WorkflowsActionType.SetWorkflowVersions, WorkflowVersionsPayload> {
  return {
    payload: {
      workflowVersions,
    },
    type: WorkflowsActionType.SetWorkflowVersions,
  };
}

export function setWorkflowDeployments(
  workflowDeployments: WorkflowDeploymentsPayload['workflowDeployments'],
): Action<WorkflowsActionType.SetWorkflowDeployments, WorkflowDeploymentsPayload> {
  return {
    payload: {
      workflowDeployments,
    },
    type: WorkflowsActionType.SetWorkflowDeployments,
  };
}

export function setTriggers(
  triggers: WorkflowTriggersPayload['triggers'],
): Action<WorkflowsActionType.SetTriggers, WorkflowTriggersPayload> {
  return {
    payload: {
      triggers,
    },
    type: WorkflowsActionType.SetTriggers,
  };
}

export function setFilters(
  filters: WorkflowsFilterParams,
): Action<WorkflowsActionType.SetFilters, SetFiltersPayload> {
  return {
    payload: {
      filters,
    },
    type: WorkflowsActionType.SetFilters,
  };
}

export function setSort(
  sortParams: SortParams,
): Action<WorkflowsActionType.SetSort, SetSortPayload> {
  return {
    payload: {
      sortParams,
    },
    type: WorkflowsActionType.SetSort,
  };
}

export type WorkflowsActions =
  | Action<WorkflowsActionType.AddWorkflow, WorkflowPayload>
  | Action<WorkflowsActionType.SetCurrentPage, CurrentPagePayload>
  | Action<WorkflowsActionType.SetFilters, SetFiltersPayload>
  | Action<WorkflowsActionType.SetPageSize, PageSizePayload>
  | Action<WorkflowsActionType.SetPaginationParams, PaginationParamsPayload>
  | Action<WorkflowsActionType.SetSort, SetSortPayload>
  | Action<WorkflowsActionType.SetTriggers, WorkflowTriggersPayload>
  | Action<WorkflowsActionType.SetWorkflows, WorkflowsPayload>
  | Action<WorkflowsActionType.SetWorkflowDeployments, WorkflowDeploymentsPayload>
  | Action<WorkflowsActionType.SetWorkflowVersions, WorkflowVersionsPayload>;
